import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, Navigate, Route, Routes } from 'react-router-dom'
import { LanguageSwitcher } from './components/LanguageSwitcher'
import { LostPanel } from './components/LostPanel'
import { LostProfileDock } from './components/LostProfileDock'
import { RadarMap } from './components/RadarMap'
import {
  AddSightingModal,
  type SightingLocation,
} from './components/AddSightingModal'
import { ChipSearchModal } from './components/ChipSearchModal'
import { ReportLostModal } from './components/ReportLostModal'
import { SightingDetailPanel } from './components/SightingDetailPanel'
import { BetaNotice } from './components/BetaNotice'
import { InformationPage } from './components/InformationPage'
import { LegalPage } from './components/LegalPage'
import { SignInModal } from './components/SignInModal'
import { useAuth } from './auth/AuthContext'
import {
  POST_AUTH_ACTION_KEY,
  type PostAuthAction,
} from './auth/providers'
import {
  fetchSightingDetail,
  fetchSightings,
  type MapSighting,
} from './lib/sightingsApi'
import { fetchLostReports, fetchAnimalImages, type LostListItem } from './lib/lostApi'
import type { Sighting } from './types/models'

function RadarPage() {
  const { t } = useTranslation()
  const { user, loading: authLoading, logout } = useAuth()
  const [panelOpen, setPanelOpen] = useState(() => {
    const narrow = window.matchMedia('(max-width: 860px)').matches
    const phoneLandscape = window.matchMedia(
      '(orientation: landscape) and (max-height: 520px)',
    ).matches
    return !narrow || phoneLandscape
  })
  const [selectedLostAnimalId, setSelectedLostAnimalId] = useState<number | null>(
    null,
  )
  const [selectedSightingId, setSelectedSightingId] = useState<number | null>(
    null,
  )
  const [sightingIndex, setSightingIndex] = useState(0)
  const [sightingDetail, setSightingDetail] = useState<Sighting | null>(null)
  const [sightingImages, setSightingImages] = useState<string[]>([])
  const [lostImages, setLostImages] = useState<string[]>([])
  const [relatedSightings, setRelatedSightings] = useState<Sighting[]>([])
  const [sightingDetailLoading, setSightingDetailLoading] = useState(false)
  const [weeksBack, setWeeksBack] = useState(3)
  const [modal, setModal] = useState<'sighting' | 'lost' | 'search' | null>(null)
  const [pickingLocationFor, setPickingLocationFor] = useState<
    'sighting' | 'lost' | null
  >(null)
  const [pickedSightingLocation, setPickedSightingLocation] =
    useState<SightingLocation | null>(null)
  const [pickedLostLocation, setPickedLostLocation] =
    useState<SightingLocation | null>(null)
  const [mapPickInstructionsHidden, setMapPickInstructionsHidden] =
    useState(false)
  const [signInAction, setSignInAction] = useState<
    PostAuthAction | null | undefined
  >(undefined)
  const [accountMenuOpen, setAccountMenuOpen] = useState(false)
  const [apiSightings, setApiSightings] = useState<MapSighting[]>([])
  const [apiLost, setApiLost] = useState<LostListItem[]>([])

  useEffect(() => {
    const narrow = window.matchMedia('(max-width: 860px)')
    const phoneLandscape = window.matchMedia(
      '(orientation: landscape) and (max-height: 520px)',
    )

    function syncLostPanel() {
      // Portrait phone: start closed. Desktop + phone landscape: keep open.
      setPanelOpen(!narrow.matches || phoneLandscape.matches)
    }

    syncLostPanel()
    narrow.addEventListener('change', syncLostPanel)
    phoneLandscape.addEventListener('change', syncLostPanel)
    return () => {
      narrow.removeEventListener('change', syncLostPanel)
      phoneLandscape.removeEventListener('change', syncLostPanel)
    }
  }, [])

  useEffect(() => {
    let cancelled = false
    const since = new Date(Date.now() - 12 * 7 * 86_400_000).toISOString()
    void Promise.all([fetchSightings(since), fetchLostReports(since)])
      .then(([sightings, lost]) => {
        if (cancelled) return
        setApiSightings(sightings)
        setApiLost(lost)
      })
      .catch(() => {
        // Map stays empty until the API is reachable again.
      })
    return () => {
      cancelled = true
    }
  }, [])

  useEffect(() => {
    if (authLoading || !user) return
    try {
      const action = window.sessionStorage.getItem(POST_AUTH_ACTION_KEY)
      if (action === 'lost' || action === 'sighting') {
        window.sessionStorage.removeItem(POST_AUTH_ACTION_KEY)
        setModal(action)
      }
    } catch {
      // The signed-in map remains usable when session storage is unavailable.
    }
  }, [authLoading, user])

  useEffect(() => {
    if (!accountMenuOpen) return
    function closeMenu(event: MouseEvent) {
      const target = event.target
      if (!(target instanceof Element)) return
      if (target.closest('.account-menu')) return
      setAccountMenuOpen(false)
    }
    document.addEventListener('mousedown', closeMenu)
    return () => document.removeEventListener('mousedown', closeMenu)
  }, [accountMenuOpen])

  useEffect(() => {
    if (!user) setAccountMenuOpen(false)
  }, [user])

  useEffect(() => {
    if (selectedSightingId == null) {
      setSightingDetail(null)
      setSightingImages([])
      setRelatedSightings([])
      setSightingDetailLoading(false)
      return
    }

    let cancelled = false
    setSightingDetailLoading(true)
    void fetchSightingDetail(selectedSightingId)
      .then((payload) => {
        if (cancelled) return
        setSightingDetail(payload.sighting)
        setSightingImages(payload.images)
        setRelatedSightings(payload.related)
        if (payload.related.length > 0) {
          const index = payload.related.findIndex(
            (row) => row.id === payload.sighting.id,
          )
          setSightingIndex(index >= 0 ? index : 0)
        } else {
          setSightingIndex(0)
        }
      })
      .catch(() => {
        if (cancelled) return
        setSightingDetail(null)
        setSightingImages([])
        setRelatedSightings([])
      })
      .finally(() => {
        if (!cancelled) setSightingDetailLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [selectedSightingId])

  useEffect(() => {
    if (selectedLostAnimalId == null) {
      setLostImages([])
      return
    }
    let cancelled = false
    void fetchAnimalImages(selectedLostAnimalId)
      .then((keys) => {
        if (!cancelled) setLostImages(keys)
      })
      .catch(() => {
        if (!cancelled) setLostImages([])
      })
    return () => {
      cancelled = true
    }
  }, [selectedLostAnimalId])

  const cutoff = useMemo(
    () => Date.now() - weeksBack * 7 * 86_400_000,
    [weeksBack],
  )
  const visibleSightings = useMemo(
    () => apiSightings.filter((s) => Date.parse(s.sighted_at) >= cutoff),
    [apiSightings, cutoff],
  )
  const visibleLost = useMemo(
    () =>
      apiLost.filter((item) => {
        const stamp = item.missing_noticed_at ?? item.created_at
        return Date.parse(stamp) >= cutoff
      }),
    [apiLost, cutoff],
  )
  const lostMapMarkers = useMemo(
    () =>
      visibleLost
        .filter(
          (item) =>
            item.last_known_lat != null && item.last_known_lng != null,
        )
        .map((item) => ({
          animal_id: item.animal_id,
          lat: item.last_known_lat as number,
          lng: item.last_known_lng as number,
        })),
    [visibleLost],
  )

  const selectedLost = useMemo(
    () =>
      selectedLostAnimalId == null
        ? null
        : (visibleLost.find((item) => item.animal_id === selectedLostAnimalId) ??
          null),
    [selectedLostAnimalId, visibleLost],
  )

  function clearSightingSelection() {
    setSelectedSightingId(null)
    setSightingIndex(0)
    setSightingDetail(null)
    setSightingImages([])
    setRelatedSightings([])
  }

  function clearLostSelection() {
    setSelectedLostAnimalId(null)
    setLostImages([])
  }

  function selectLost(animalId: number) {
    clearSightingSelection()
    setSelectedLostAnimalId(animalId)
    if (window.matchMedia('(max-width: 860px)').matches) {
      setPanelOpen(false)
    }
  }

  function openSighting(animalId: number | null, sightingId: number) {
    if (sightingId > 0) {
      clearLostSelection()
      setSelectedSightingId(sightingId)
      if (window.matchMedia('(max-width: 860px)').matches) {
        setPanelOpen(false)
      }
      return
    }
    if (animalId != null) selectLost(animalId)
  }

  function stepRelated(delta: number) {
    if (relatedSightings.length === 0) return
    const nextIndex = Math.min(
      relatedSightings.length - 1,
      Math.max(0, sightingIndex + delta),
    )
    const next = relatedSightings[nextIndex]
    if (!next) return
    setSightingIndex(nextIndex)
    setSelectedSightingId(next.id)
  }

  function requestPost(action: PostAuthAction) {
    if (authLoading) return
    if (!user) {
      setSignInAction(action)
      return
    }
    if (action === 'sighting' || action === 'lost') {
      setPickingLocationFor(null)
      setMapPickInstructionsHidden(false)
    }
    setModal(action)
  }

  function accountLabel(full = false) {
    if (!user) return t('nav.login')
    const name = user.display_name?.trim()
    if (name) {
      const firstName = name.split(/\s+/)[0]
      return full ? name : firstName
    }
    return user.email || t('auth.account')
  }

  return (
    <div className="app-shell">
      <header className="chrome">
        <Link className="brand-lockup" to="/" aria-label={t('brand')}>
          <img
            className="brand-logo"
            src="/brand/logo-header.webp"
            alt=""
            width="48"
            height="48"
          />
          <div className="brand-block">
            <div className="brand">
              Aruba <span>Animal</span> Radar
            </div>
            <div className="brand-sub">{t('tagline')}</div>
          </div>
        </Link>
        <div className="chrome-actions">
          <Link className="btn" to="/information">
            {t('nav.information')}
          </Link>
          <button type="button" className="btn" onClick={() => setModal('search')}>
            {t('nav.search')}
          </button>
          <button
            type="button"
            className="btn"
            disabled={authLoading}
            onClick={() => requestPost('lost')}
          >
            {t('nav.reportLost')}
          </button>
          <button
            type="button"
            className="btn btn-primary"
            disabled={authLoading}
            onClick={() => requestPost('sighting')}
          >
            {t('nav.addSighting')}
          </button>
          <LanguageSwitcher />
        </div>
        <div className="account-menu">
          <button
            type="button"
            className={`account-icon${user ? ' signed-in' : ''}${accountMenuOpen ? ' open' : ''}`}
            disabled={authLoading}
            aria-haspopup={user ? 'menu' : undefined}
            aria-expanded={user ? accountMenuOpen : undefined}
            onClick={() => {
              if (!user) {
                setSignInAction(null)
                return
              }
              setAccountMenuOpen((open) => !open)
            }}
            aria-label={user ? t('auth.account') : t('nav.login')}
            title={accountLabel(true)}
          >
            <svg viewBox="0 0 24 24" aria-hidden="true">
              {user ? (
                <>
                  <circle cx="12" cy="8" r="4.2" />
                  <path d="M4 21c0-4.4 3.6-8 8-8s8 3.6 8 8" />
                </>
              ) : (
                <>
                  <circle cx="12" cy="8" r="4" />
                  <path d="M4.5 21a7.5 7.5 0 0 1 15 0" />
                </>
              )}
            </svg>
          </button>
          {user && accountMenuOpen && (
            <div className="account-menu-panel" role="menu">
              <div className="account-menu-label">{accountLabel()}</div>
              <button
                type="button"
                role="menuitem"
                onClick={() => {
                  setAccountMenuOpen(false)
                  void logout()
                }}
              >
                {t('nav.logout')}
              </button>
            </div>
          )}
        </div>
      </header>

      <main className="workspace">
        <section
          className={`map-stage${selectedSightingId || selectedLost ? ' profile-dock-open' : ''}`}
          aria-label={t('nav.map')}
        >
          <div className="scanline" />
          <button
            type="button"
            className="mobile-lost-toggle"
            aria-expanded={panelOpen}
            aria-controls="active-lost-panel"
            onClick={() => setPanelOpen((open) => !open)}
          >
            <span>
              {t('panel.lostTitle')}
              <strong>{visibleLost.length}</strong>
            </span>
            <span className="mobile-lost-chevron" aria-hidden="true">
              {panelOpen ? '▲' : '▼'}
            </span>
          </button>
          <div className="map-range-control">
            <label htmlFor="weeks-back">
              {t('map.showHistory')}: <strong>{t('map.weeks', { count: weeksBack })}</strong>
            </label>
            <input
              id="weeks-back"
              type="range"
              min="1"
              max="12"
              step="1"
              value={weeksBack}
              onChange={(event) => setWeeksBack(Number(event.target.value))}
            />
          </div>
          {pickingLocationFor && (
            <div
              className={`map-pick-banner${mapPickInstructionsHidden ? ' collapsed' : ''}`}
              role="status"
            >
              {!mapPickInstructionsHidden && (
                <p className="map-pick-instructions">
                  {t(
                    pickingLocationFor === 'lost'
                      ? 'lost.tapMapLocation'
                      : 'sighting.tapMapLocation',
                  )}
                </p>
              )}
              <div className="map-pick-actions">
                <button
                  type="button"
                  className="btn"
                  onClick={() => {
                    const resume = pickingLocationFor
                    setPickingLocationFor(null)
                    setMapPickInstructionsHidden(false)
                    setModal(resume)
                  }}
                >
                  {t('actions.cancel')}
                </button>
                {mapPickInstructionsHidden ? (
                  <button
                    type="button"
                    className="btn"
                    onClick={() => setMapPickInstructionsHidden(false)}
                  >
                    {t('actions.showInstructions')}
                  </button>
                ) : (
                  <button
                    type="button"
                    className="btn"
                    onClick={() => setMapPickInstructionsHidden(true)}
                  >
                    {t('actions.hideInstructions')}
                  </button>
                )}
              </div>
            </div>
          )}
          <RadarMap
            highlightedSightingId={selectedSightingId}
            highlightedLostAnimalId={selectedLostAnimalId}
            sightings={visibleSightings}
            lostMarkers={lostMapMarkers}
            pickingLocation={pickingLocationFor != null}
            onMarkerClick={openSighting}
            onLocationPicked={(location) => {
              if (pickingLocationFor === 'lost') {
                setPickedLostLocation(location)
                setPickingLocationFor(null)
                setModal('lost')
                return
              }
              setPickedSightingLocation(location)
              setPickingLocationFor(null)
              setModal('sighting')
            }}
          />
          {selectedLost && (
            <LostProfileDock
              item={selectedLost}
              images={lostImages}
              onClose={clearLostSelection}
              onDeleted={() => {
                setApiLost((current) =>
                  current.filter((row) => row.id !== selectedLost.id),
                )
                clearLostSelection()
              }}
              onFound={() => {
                setApiLost((current) =>
                  current.filter((row) => row.id !== selectedLost.id),
                )
                clearLostSelection()
              }}
              onUpdated={(updated) => {
                setApiLost((current) =>
                  current.map((row) =>
                    row.id === updated.id ? updated : row,
                  ),
                )
              }}
            />
          )}
          {selectedSightingId != null && sightingDetail && (
            <SightingDetailPanel
              sighting={sightingDetail}
              images={sightingImages}
              relatedCount={relatedSightings.length}
              relatedIndex={sightingIndex}
              loading={sightingDetailLoading}
              onClose={clearSightingSelection}
              onStepPrev={() => stepRelated(-1)}
              onStepNext={() => stepRelated(1)}
              onDeleted={() => {
                const deletedId = selectedSightingId
                setApiSightings((current) =>
                  current.filter((row) => row.id !== deletedId),
                )
                clearSightingSelection()
              }}
              onUpdated={(updated) => {
                setSightingDetail(updated)
              }}
            />
          )}
          {selectedSightingId != null &&
            !sightingDetail &&
            sightingDetailLoading && (
              <section className="profile-dock" aria-busy="true">
                <div className="panel-body profile-dock-body">
                  <p className="empty-state">{t('status.loading')}</p>
                </div>
              </section>
            )}
        </section>

        <LostPanel
          open={panelOpen}
          items={visibleLost}
          selectedAnimalId={selectedLostAnimalId}
          onSelect={selectLost}
          onClose={() => setPanelOpen(false)}
        />
      </main>

      <footer className="map-footnote">
        <Link to="/privacy">{t('legal.privacy')}</Link>
        <span aria-hidden="true">·</span>
        <Link to="/terms">{t('legal.terms')}</Link>
        {user && (
          <>
            <span aria-hidden="true">·</span>
            <button type="button" onClick={() => void logout()}>
              {t('nav.logout')}
            </button>
          </>
        )}
      </footer>

      <AddSightingModal
        open={modal === 'sighting'}
        onClose={() => setModal(null)}
        pickedMapLocation={pickedSightingLocation}
        onRequestMapPick={() => {
          setModal(null)
          setPickingLocationFor('sighting')
          setMapPickInstructionsHidden(false)
          setPanelOpen(false)
        }}
        onSubmitted={(sighting) => {
          setApiSightings((current) => {
            const next = current.filter((row) => row.id !== sighting.id)
            return [sighting, ...next]
          })
          setPickedSightingLocation(null)
        }}
      />
      <ReportLostModal
        open={modal === 'lost'}
        onClose={() => setModal(null)}
        pickedMapLocation={pickedLostLocation}
        onRequestMapPick={() => {
          setModal(null)
          setPickingLocationFor('lost')
          setMapPickInstructionsHidden(false)
          setPanelOpen(false)
        }}
        onSubmitted={(item) => {
          setApiLost((current) => {
            const next = current.filter((row) => row.id !== item.id)
            return [item, ...next]
          })
          setPickedLostLocation(null)
        }}
      />
      <ChipSearchModal open={modal === 'search'} onClose={() => setModal(null)} />
      <SignInModal
        open={signInAction !== undefined}
        intendedAction={signInAction ?? null}
        onClose={() => setSignInAction(undefined)}
      />
    </div>
  )
}

export default function App() {
  return (
    <>
      <Routes>
        <Route path="/" element={<RadarPage />} />
        <Route path="/information" element={<InformationPage />} />
        <Route path="/privacy" element={<LegalPage kind="privacy" />} />
        <Route path="/terms" element={<LegalPage kind="terms" />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      <BetaNotice />
    </>
  )
}
